import { PropsWithChildren, useEffect } from "react";

import { css } from "@compiled/react";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";
import { AtlassianLogo } from "@atlaskit/logo";
import { media } from "@atlaskit/primitives";
import { token } from "@atlaskit/tokens";

import { useExperiment, experiments } from "@/utils/experiment";
import { useIsMobile } from "@/utils/useIsMobile";

import { CobrandedProductLogo } from "../bxp-ui-signup/src/components/site-name-form/components/variants/new-user-confluence-workspace-name";

import { CFind3467SignupBackgroundLayout } from "./cfind3467/signup-background-layout";

export interface SignupBackgroundLayoutProps extends PropsWithChildren {
    ProductLogo: JSX.Element;
    page?: string;
}

export const SignupBackgroundLayout = ({
    ProductLogo,
    children,
    page
}: SignupBackgroundLayoutProps) => {
    const { createAnalyticsEvent } = useAnalyticsEvents();
    const isDesktop = !useIsMobile(800);
    const signUpWithImageOverlayExperiment = useExperiment(
        experiments.cfind3467SignUpWithImageOverlay
    );

    const isConfluence = ProductLogo.type === CobrandedProductLogo;
    const isEnrolledInImageOverlayExp =
        isDesktop &&
        isConfluence &&
        signUpWithImageOverlayExperiment &&
        signUpWithImageOverlayExperiment.type === "enrolled";

    useEffect(() => {
        if (isEnrolledInImageOverlayExp) {
            createAnalyticsEvent({
                page,
                action: "enrolled",
                subject: "cfind-3467",
                attributes: {
                    group: signUpWithImageOverlayExperiment.value
                        ? "treatment"
                        : "control"
                }
            });
        }
    }, [
        createAnalyticsEvent,
        isEnrolledInImageOverlayExp,
        page,
        signUpWithImageOverlayExperiment
    ]);

    if (isEnrolledInImageOverlayExp) {
        signUpWithImageOverlayExperiment.fireExposure();

        if (signUpWithImageOverlayExperiment.value) {
            return (
                <CFind3467SignupBackgroundLayout>
                    {children}
                </CFind3467SignupBackgroundLayout>
            );
        }
    }

    return (
        <div css={containerStyles} data-testid="site-name-form">
            {ProductLogo}
            <div>
                <div css={formWrapperCss}>{children}</div>
            </div>
            <AtlassianLogo
                appearance="neutral"
                size="small"
                testId="atlassian-logo"
                label="Atlassian logo"
            />
        </div>
    );
};

const containerStyles = css({
    display: "grid",
    gap: "25px",
    justifyItems: "center",
    gridTemplateRows: "auto 1fr auto",
    backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 1440 518' preserveAspectRatio='none' fill='%23E9F2FE' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1331.5 -20.8369L-38.614 -36.9811L-43.579 433.588H243.576L248.105 518L1598.49 435.131L1574.37 -14.3789L1331.5 0.525597V-20.8369Z'/%3E%3C/svg%3E%0A")`,
    backgroundColor: token("elevation.surface"),
    backgroundSize: "100% 120px",
    backgroundPosition: "center 0",
    backgroundRepeat: "no-repeat",
    minHeight: "100vh",
    padding: "50px 0 100px",
    [media.above.md]: {
        backgroundSize: "100% 420px"
    }
});

const formWrapperCss = css({
    backgroundColor: token("elevation.surface"),
    borderRadius: "8px",
    boxSizing: "content-box",
    margin: "0 auto",
    maxWidth: "calc(100vw - 2rem)",
    padding: "1rem",
    width: 400,
    [media.above.md]: {
        // using wac token fails vr tests
        // eslint-disable-next-line local/ensure-wac-token-usage
        boxShadow: "0px 0px 8px 0px #091E4229",
        padding: "40px 80px",
        minHeight: 520
    }
});
